var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};

// x-dropdown.ts
import { isSafari } from "@adornis/base/client/frontend-helpers/window-size.js";
import { ChemistryLitElement } from "@adornis/chemistry/chemistry-lit-element.js";
import { XNativeDialog } from "@adornis/dialog/x-native-dialog.js";
import { html } from "lit";
import { customElement, property } from "lit/decorators.js";
import { BehaviorSubject } from "rxjs";
import tippy from "tippy.js";
import "./tippy-styles.css";
var dropdownParentMap = /* @__PURE__ */ new WeakMap();
var reverseDropdownMap = /* @__PURE__ */ new WeakMap();
var addToParentMap = (node, element) => {
  let array = dropdownParentMap.get(node);
  if (!array)
    dropdownParentMap.set(node, array = []);
  array.push(element);
  reverseDropdownMap.set(element, node);
};
var removeFromParentMap = (element) => {
  const node = reverseDropdownMap.get(element);
  if (!node)
    return;
  const array = dropdownParentMap.get(node);
  if (!array)
    return;
  const index = array.findIndex((elem) => elem === element);
  if (index > -1)
    array.splice(index, 1);
  if (!array.length)
    dropdownParentMap.delete(node);
  reverseDropdownMap.delete(element);
};
var XDropdown = class extends ChemistryLitElement {
  constructor() {
    super();
    this["manual-close"] = false;
    this.clickTriggerToToggle = false;
    this.isOpen$ = new BehaviorSubject(false);
    this._mouseDownOnContent = false;
    this.options = {};
    this.style.display = "block";
  }
  set content(content) {
    if (this._content === content)
      return;
    this._content = content;
    if (this._content.receiveTrigger)
      this._content.receiveTrigger(this);
    if (this._content.eventTarget)
      this._content.eventTarget = this;
    this._content.addEventListener("mousedown", () => this._mouseDownOnContent = true);
    this._content.addEventListener("mouseup", () => this._mouseDownOnContent = false);
    this.reinit();
  }
  get content() {
    return this._content;
  }
  _defaultOptions() {
    return {
      content: this.content,
      placement: "bottom-start",
      interactive: true,
      trigger: this.clickTriggerToToggle ? "click" : "manual",
      triggerTarget: this,
      appendTo: window.document.body,
      animation: isSafari() ? void 0 : "shift-away",
      // no animation for safari b/c shadows are kinda broken there
      maxWidth: "none",
      hideOnClick: this.clickTriggerToToggle,
      theme: "custom",
      arrow: false,
      duration: [200, 300],
      delay: [0, 0],
      onClickOutside: (instance, event) => {
        if (this.wouldEventCloseMe(event, instance))
          this.close();
      },
      zIndex: 1003
    };
  }
  connectedCallback() {
    super.connectedCallback();
    const isPopperElement = (node) => node._tippy && node._tippy.reference !== node;
    const parentPopper = findParentOf(this, isPopperElement);
    if (parentPopper)
      addToParentMap(parentPopper, this);
    this.reinit();
  }
  reinit() {
    this.tippy?.destroy();
    this.tippy = tippy(this, {
      ...this._defaultOptions(),
      appendTo: this._lookForDialogElementInParents(),
      ...this.options,
      popperOptions: {
        modifiers: [
          {
            name: "preventOverflow",
            options: {
              boundary: "viewport"
              // Verwende den gesamten Viewport als Grenze
            }
          }
        ]
      },
      onShow: () => {
        this.dispatchEvent(new CustomEvent("open"));
        this.isOpen$.next(true);
      },
      onHide: () => {
        this.dispatchEvent(new CustomEvent("close"));
        this.isOpen$.next(false);
      }
    });
  }
  isOpen() {
    return this.tippy?.state.isVisible || false;
  }
  wouldEventCloseMe(event, instance) {
    if (this["manual-close"])
      return;
    if (this._mouseDownOnContent)
      return;
    if (!instance)
      instance = this.tippy;
    const path = event.composedPath();
    const isMyContent = path.includes(this.content);
    const childDropdowns = instance ? dropdownParentMap.get(instance.popper)?.map((dropdown) => dropdown.tippy?.popper).filter(Boolean) : void 0;
    const isChild = !!childDropdowns && !!event.composedPath().find((path2) => {
      return childDropdowns.includes(path2);
    });
    return !isMyContent && !isChild;
  }
  async open() {
    this.tippy?.show();
  }
  close() {
    return new Promise((resolve) => {
      this.tippy?.hide();
      setTimeout(
        () => requestAnimationFrame(() => resolve()),
        Array.isArray(this.tippy?.props.duration) ? this.tippy?.props.duration[1] ?? 250 : this.tippy?.props.duration
      );
    });
  }
  toggle() {
    if (this.tippy?.state.isVisible)
      this.close();
    else
      this.open();
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    removeFromParentMap(this);
    this.tippy?.destroy();
  }
  update(changedProperties) {
    super.update(changedProperties);
    const slotted = this.querySelector("[slot=dropdown-content]");
    if (!this.content && slotted)
      this.content = slotted;
  }
  render() {
    return html`
      <slot></slot>
      <slot name="trigger"></slot>

      <slot
        name="dropdown-content"
        @slotchange=${(e) => {
      const thisSlot = e.currentTarget;
      const contentElement = Array.from(thisSlot.assignedNodes())[0];
      if (!contentElement || this.content === contentElement)
        return;
      this.content = contentElement;
    }}
      ></slot>
    `;
  }
  /**
   * TODO use native dialog element for our dropdowns
   * ! This is a dirty hack to determine if this dropdown is inside a native dialog element
   * -> If so, we attach tippy to said dialog element
   */
  _lookForDialogElementInParents() {
    let curr = this;
    while (
      // @ts-expect-error host exists on rootNode in this context
      (curr = curr.parentElement || curr.getRootNode()?.host) && !(curr instanceof HTMLDialogElement) && !(curr instanceof XNativeDialog)
    ) {
    }
    if (curr instanceof HTMLDialogElement)
      return curr;
    if (curr instanceof XNativeDialog) {
      const dialog = curr.renderRoot.querySelector("dialog");
      return dialog ?? curr;
    }
    return window.document.body;
  }
};
__decorateClass([
  property({ type: Boolean })
], XDropdown.prototype, "manual-close", 2);
__decorateClass([
  property({ type: Boolean, attribute: "click-trigger-to-toggle" })
], XDropdown.prototype, "clickTriggerToToggle", 2);
XDropdown = __decorateClass([
  customElement("x-dropdown")
], XDropdown);
var getActiveElement = (root = document) => {
  const activeEl = root.activeElement;
  if (!activeEl)
    return null;
  if (activeEl.shadowRoot)
    return getActiveElement(activeEl.shadowRoot) || activeEl;
  else
    return activeEl;
};
var isShadowRoot = (node) => !!node.host;
var findParentOf = (element, filter) => {
  if (isShadowRoot(element))
    return findParentOf(element.host, filter);
  if (filter(element))
    return element;
  if (!element.parentNode)
    return;
  return findParentOf(element.parentNode, filter);
};
export {
  XDropdown,
  findParentOf,
  getActiveElement
};
